import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { InputAdornment, TextField } from '@mui/material';
import { Close, Save } from '@mui/icons-material';
import { FaUser } from 'react-icons/fa';
import Popup from 'reactjs-popup';

import EnhancedPage from '../../Commen_Component/EnhancedPage';
import { PageEnums } from '../../../_enums/enum.services';
import './AddProfile.css';
import '../EmailTemplate/AddEmailTemplate.css';
import { ProfileMasterService } from '../../Commen_Services/ProfileMaster.service';
import customToast from '../../Commen_Component/Toast/CustomToast';
import { useLoading } from '../../Loader/LoadingContext';

interface ProfileFormData {
    profileId: number;
    name: string;
    mobileNo: string;
    isActive: boolean;
}

const emptyFormData: ProfileFormData = {
    profileId: 0,
    name: '',
    mobileNo: '',
    isActive: true,
};

const AddProfile = () => {
    const navigate = useNavigate();
    const { pageId, action } = useParams<{ pageId: string; action: string }>();
    const { setLoading } = useLoading();
    const [formData, setFormData] = useState<ProfileFormData>(emptyFormData);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showConfirmation, setShowConfirmation] = useState(false);
    const isViewData = action === 'view';
    const isEditMode = Number(pageId) > 0;
    const isListRoute = pageId === undefined && action === undefined;

    useEffect(() => {
        setShowConfirmation(false);
    }, [pageId, action]);

    useEffect(() => {
        const profileId = Number(pageId);
        if (!Number.isInteger(profileId) || profileId <= 0) {
            setFormData(emptyFormData);
            setErrors({});
            return;
        }

        const loadProfile = async () => {
            try {
                setLoading(true);
                const response = await ProfileMasterService.getProfileDetail(profileId);
                if (response?.status === true && response?.data) {
                    setFormData({
                        profileId: response.data.profileId,
                        name: response.data.name ?? '',
                        mobileNo: String(response.data.mobileNo ?? response.data.phone ?? '')
                            .replace(/\s/g, '')
                            .replace(/^\+91/, ''),
                        isActive: response.data.isActive ?? true,
                    });
                } else {
                    customToast.Warning(response?.message || 'Profile details were not found.');
                }
            } catch {
                customToast.error('Unable to load profile details.');
            } finally {
                setLoading(false);
            }
        };

        loadProfile();
    }, [pageId, setLoading]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, checked, type } = event.target;
        if (name === 'mobileNo') {
            if (!/^\d{0,10}$/.test(value)) {
                return;
            }
        } else if (name === 'name' && !/^[\p{L}\p{N} ]*$/u.test(value)) {
            return;
        }

        setFormData((current) => ({
            ...current,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) {
            setErrors((current) => ({ ...current, [name]: '' }));
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isViewData) return;

        const nextErrors: Record<string, string> = {};
        const profileName = formData.name.trim();
        if (!profileName) {
            nextErrors.name = 'Please enter a profile name.';
        } else if (!/^[\p{L}\p{N} ]+$/u.test(profileName)) {
            nextErrors.name = 'Only letters, numbers, and spaces are allowed.';
        }
        if (!/^\d{10}$/.test(formData.mobileNo)) {
            nextErrors.mobileNo = 'Enter a valid 10-digit mobile number.';
        }
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;

        try {
            setLoading(true);
            const response = await ProfileMasterService.saveOrUpdateProfileMaster({
                ...formData,
                name: formData.name.trim(),
                mobileNo: `+91${formData.mobileNo}`,
            });
            if (response?.status) {
                customToast.success(response.message);
                navigate('/Master/ProfileMaster');
            } else {
                customToast.error(response.message);
            }
        } catch {
            customToast.error('Unable to save profile details');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => setShowConfirmation(false);
    const handleConfirmExit = () => navigate('/Master/ProfileMaster');

    if (isListRoute) {
        return <EnhancedPage pageId={PageEnums.ProfileMaster} />;
    }

    return (
        <div>
            <div className="shadow">
                <div className="bg-line">
                    <span className="bg-head">
                        <FaUser /> &nbsp;{isViewData ? 'View' : isEditMode ? 'Update' : 'Add'} Profile
                    </span>
                </div>
                <hr className="colorgraph" />
            </div>

            <form className="profile-form" onSubmit={handleSubmit}>
                <div className="profile-form__field">
                    <TextField
                        variant="standard"
                        name="name"
                        value={formData.name}
                        label="Profile Name *"
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        fullWidth
                        InputProps={{ readOnly: isViewData }}
                    />
                </div>
                <div className="profile-form__field">
                    <TextField
                        variant="standard"
                        name="mobileNo"
                        value={formData.mobileNo}
                        label="Mobile Number *"
                        onChange={handleChange}
                        error={!!errors.mobileNo}
                        helperText={errors.mobileNo}
                        fullWidth
                        inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                        InputProps={{
                            readOnly: isViewData,
                            startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                        }}
                    />
                </div>
                <div className="profile-form__active-row">
                    <div className="Emp_isActive">
                        <label className="Emp_Switch">
                            <input
                                type="checkbox"
                                name="isActive"
                                checked={formData.isActive}
                                onChange={handleChange}
                                disabled={isViewData}
                            />
                            <span className="Emp_slider"></span>
                        </label>
                        <span className="profile-form__active-label">
                            Is Active
                        </span>
                    </div>
                </div>

                <div className="Area_btn profile-form__actions">
                    {!isViewData && (
                        <button className="btn123 profile-form__button" type="submit">
                            <Save />&nbsp;{isEditMode ? 'Update' : 'Save'}
                        </button>
                    )}
                    &nbsp;
                    <button
                        className="btn123 profile-form__button"
                        type="button"
                        onClick={() => setShowConfirmation(true)}
                    >
                        <Close />&nbsp;Close
                    </button>

                    {showConfirmation && (
                        <Popup
                            className="profile-exit-popup"
                            position="top center"
                            open={showConfirmation}
                            onClose={handleClose}
                        >
                            <div className="confirmation-modal">
                                <p>Are you sure you want to exit? <br />(Any unsaved changes will be lost.)</p>
                                <div className="canel_btns">
                                    <button className="btn123 profile-exit-popup__cancel" type="button" onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <button className="btn123 profile-exit-popup__confirm" type="button" onClick={handleConfirmExit}>
                                        Yes, Exit
                                    </button>
                                </div>
                            </div>
                        </Popup>
                    )}
                </div>
            </form>
        </div>
    );
};

export default AddProfile;
